import { AxiosError, AxiosResponse } from 'axios';
import { notificationService } from '@/lib/services/notification.service';
import { logger } from '@/lib/utils/logger';

// eslint-disable-next-line i18next/no-literal-string -- request header name
export const SUPPRESS_ERROR_NOTIFICATION_HEADER = 'X-Suppress-Error-Notification';

/**
 * The status-list form of the suppression header, for a call that renders some refusals itself
 * but must still toast the rest. No statuses sends no header, so the toast stays.
 */
export const suppressErrorNotificationFor = (
  statuses: readonly number[] | undefined,
): Record<string, string> =>
  statuses && statuses.length > 0
    ? { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: statuses.join(',') }
    : {};

interface ProblemDetails {
  status: number;
  type: string;
  title: string;
  detail: string;
  timestamp: string;
  [key: string]: unknown;
}

/**
 * Whether the suppression header covers this response's status.
 *
 * Anything that is not a list of status codes - `'true'`, as almost every caller sends -
 * suppresses the toast for every problem-details response, because those callers render
 * the failure themselves whatever it is. A comma-separated list suppresses only those
 * statuses, which is for a caller that owns one ordinary refusal but has nowhere to report
 * a fault it did not expect: it keeps its 404 quiet and lets a 500 through.
 */
function suppresses(value: unknown, status: number): boolean {
  if (!value) return false;
  const statuses = String(value)
    .split(',')
    .map((entry) => Number(entry.trim()));
  return statuses.some(Number.isNaN) || statuses.includes(status);
}

export const problemDetailsInterceptor = {
  onResponse: (response: AxiosResponse) => response,
  onError: (error: AxiosError) => {
    if (error.response?.data) {
      const data = error.response.data;
      // Check if the error follows the Problem Details RFC format
      if (
        typeof data === 'object' &&
        'status' in data &&
        'type' in data &&
        'title' in data &&
        'detail' in data &&
        'timestamp' in data
      ) {
        const problemDetails = data as ProblemDetails;
        const { title, detail, status } = problemDetails;

        // Don't handle 401 errors here - let the refresh token interceptor handle them
        if (status === 401) {
          // Just log for debugging and let the error pass through
          logger.warn(
            'Unauthorized access detected, letting refresh token interceptor handle it...',
          );
          return Promise.reject(error);
        }

        const suppressNotification = suppresses(
          error.config?.headers[SUPPRESS_ERROR_NOTIFICATION_HEADER],
          status,
        );

        // Show the error notification for other errors using the notification service
        if (!suppressNotification) {
          notificationService.showError(detail || title, {
            persist: true,
          });
        }

        // Also log for debugging
        logger.error('API Error (Problem Details):', {
          title,
          detail,
          status: problemDetails.status,
          type: problemDetails.type,
        });
      }
    }

    return Promise.reject(error);
  },
};
